import { Form, Link, NavLink, useParams, useSubmit } from '@remix-run/react'
import { motion, useScroll } from 'framer-motion'
import React, { useEffect, useRef, useState } from 'react'
import { Icon } from '#app/components/ui/icon.tsx'
import { getBestRole } from '#app/utils/misc.tsx'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu.tsx'

type NavItem = {
	to: string
	label: string
	dropdown?: Array<{ to: string; label: string }>
}

export const baseNavItems: NavItem[] = [
	{ to: '/about', label: 'About' },
	{ to: '/competitions', label: 'Competitions' },
	{ to: '/run-club', label: 'Run Club & Wellness' },
	{ to: '/communities', label: 'Communities' },
	{ to: '/rules', label: 'Game Rules' },
]

const getNavItems = (bestRole: string, isLoggedIn: boolean) => {
	// Only show 'Switch Profiles' for logged in users with the USER role
	if (isLoggedIn && bestRole === 'USER') {
		return [...baseNavItems, { to: '/onboarding', label: 'Switch Profiles' }]
	}
	return baseNavItems
}

const ModernNav = ({ user }: { user: any }) => {
	const { competitionId } = useParams<{ competitionId: string }>()
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
	const [hasScrolled, setHasScrolled] = useState(false)
	const submit = useSubmit()
	const formRef = useRef<HTMLFormElement>(null)
	const bestRole = getBestRole(user?.roles || [])
	const isLoggedIn = Boolean(user)

	// Scroll animation with smoother transitions
	const { scrollY } = useScroll()

	// Update hasScrolled state based on scroll position
	useEffect(() => {
		const updateScrollState = () => {
			setHasScrolled(scrollY.get() > 50)
		}

		const unsubscribe = scrollY.onChange(updateScrollState)
		return () => unsubscribe()
	}, [scrollY])

	// Get navigation items based on user role
	const navItems = competitionId
		? [
			{
				to: `/competitions/${user?.organization?.slug}/clubs`,
				label: 'Clubs',
			},
			{
				to: `/competitions/${user?.organization?.slug}/matches`,
				label: 'Fixtures',
			},
			{
				to: `/competitions/${user?.organization?.slug}/players`,
				label: 'Players',
			},
			{
				to: `/competitions/${user?.organization?.slug}/media`,
				label: 'Media',
			},
			{
				to: `/competitions/${user?.organization?.slug}/stats`,
				label: 'Stats',
			},
			{
				to: `/competitions/${user?.organization?.slug}/transfers`,
				label: 'Transfers',
			},
		]
		: getNavItems(bestRole, isLoggedIn)

	// Handle body scroll lock for mobile menu
	useEffect(() => {
		if (isMobileMenuOpen) {
			document.body.style.overflow = 'hidden'
		} else {
			document.body.style.overflow = 'unset'
		}

		return () => {
			document.body.style.overflow = 'unset'
		}
	}, [isMobileMenuOpen])

	return (
		<>
			{/* Main Navigation */}
			<motion.header
				className="fixed left-0 right-0 top-0 z-50 flex h-16 items-center justify-center px-3"
				initial={{ width: '100%', borderRadius: '0px' }}
				animate={{
					width: hasScrolled ? '95%' : '100%',
					borderRadius: hasScrolled ? '12px' : '0px',
					backgroundColor: hasScrolled
						? 'rgba(15, 15, 15, 0.9)'
						: 'rgba(0, 0, 0, 0)',
					backdropFilter: hasScrolled ? 'blur(10px)' : 'blur(0px)',
					boxShadow: hasScrolled ? '0 10px 30px rgba(0, 0, 0, 0.2)' : 'none',
				}}
				transition={{
					duration: 0.2,
					ease: [0.25, 0.1, 0.25, 1.0],
				}}
				style={{
					left: '50%',
					transform: 'translateX(-50%)',
				}}
			>
				<nav className="flex w-full items-center justify-between">
					{/* Logo */}
					<Link to="/" className="flex items-center">
						<img
							src="/img/logos/logo.png"
							alt="7even Sports"
							className="h-8 w-auto"
						/>
					</Link>

					{/* Desktop Navigation - Always visible */}
					<div className="hidden md:flex md:items-center md:space-x-6">
						{navItems.map((item) =>
							'dropdown' in item && item.dropdown ? (
								<DropdownMenu key={item.to}>
									<DropdownMenuTrigger asChild>
										<button className="flex items-center space-x-1 text-sm font-medium text-white/90 transition-colors duration-200 hover:text-primary">
											<span>{item.label}</span>
											<Icon name="chevron-down" className="size-4" />
										</button>
									</DropdownMenuTrigger>
									<DropdownMenuContent
										align="center"
										className="w-48 border-background-dark bg-background-dark-foreground"
									>
										<DropdownMenuItem asChild>
											<Link
												to={item.to}
												className="w-full text-white/80 hover:text-white"
											>
												Overview
											</Link>
										</DropdownMenuItem>
										<DropdownMenuSeparator className="bg-background-dark/60" />
										{item.dropdown.map((subItem) => (
											<DropdownMenuItem key={subItem.to} asChild>
												<Link
													to={subItem.to}
													className="w-full text-white/80 hover:text-white"
												>
													{subItem.label}
												</Link>
											</DropdownMenuItem>
										))}
									</DropdownMenuContent>
								</DropdownMenu>
							) : (
								<NavLink
									key={item.to}
									to={item.to}
									className={({ isActive }) =>
										isActive
											? `text-sm font-bold text-primary`
											: `text-sm font-medium text-white/90 transition-colors duration-200 hover:text-primary`
									}
								>
									{item.label}
								</NavLink>
							),
						)}
					</div>

					{/* Auth Buttons or User Menu */}
					<div className="hidden md:block">
						{user ? (
							<div className="flex items-center">
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<button className="flex items-center gap-2 rounded-md bg-background-dark-foreground/60 px-3 py-1.5 text-white/90 transition-colors duration-200 hover:bg-background-dark focus:outline-none">
											{user.avatar ? (
												<img
													src={user.avatar}
													alt={user.name}
													className="h-7 w-7 rounded-full object-cover"
												/>
											) : (
												<div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-white">
													{user.name?.charAt(0).toUpperCase()}
												</div>
											)}
											<span className="text-sm font-medium">{user.name}</span>
											<Icon
												name="chevron-down"
												className="size-4 text-white/60"
											/>
										</button>
									</DropdownMenuTrigger>

									<DropdownMenuContent
										align="end"
										className="w-48 border-background-dark bg-background-dark-foreground"
									>
										{bestRole === 'USER' && (
											<DropdownMenuItem asChild>
												<Link
													to="/settings/profile"
													className="text-white/80 hover:text-white"
												>
													Edit Profile
												</Link>
											</DropdownMenuItem>
										)}

										{bestRole === 'PLAYER' && (
											<>
												<DropdownMenuItem asChild>
													<Link
														to={`/player/${user?.playerProfile?.id}`}
														className="text-white/80 hover:text-white"
													>
														View Profile
													</Link>
												</DropdownMenuItem>
												<DropdownMenuItem asChild>
													<Link
														to={`/player/${user?.playerProfile?.id}/edit/personal-info`}
														className="text-white/80 hover:text-white"
													>
														Edit Profile
													</Link>
												</DropdownMenuItem>
												<DropdownMenuItem asChild>
													<Link
														to="/settings"
														className="text-white/80 hover:text-white"
													>
														Settings
													</Link>
												</DropdownMenuItem>
											</>
										)}

										{bestRole === 'TEAM_OWNER' && (
											<>
												<DropdownMenuItem asChild>
													<Link
														to="/settings/profile"
														className="text-white/80 hover:text-white"
													>
														Edit Profile
													</Link>
												</DropdownMenuItem>
												<DropdownMenuItem asChild>
													<Link
														to={`/team/${user?.team[0]?.slug}/summary`}
														className="text-white/80 hover:text-white"
													>
														View Team
													</Link>
												</DropdownMenuItem>
												<DropdownMenuItem asChild>
													<Link
														to={`/team/${user?.team[0]?.slug}/settings`}
														className="text-white/80 hover:text-white"
													>
														Edit Team
													</Link>
												</DropdownMenuItem>
											</>
										)}

										{bestRole === 'ORGANIZATION_OWNER' && (
											<>
												<DropdownMenuItem asChild>
													<Link
														to="/settings/profile"
														className="text-white/80 hover:text-white"
													>
														Edit Profile
													</Link>
												</DropdownMenuItem>
												<DropdownMenuItem asChild>
													<Link
														to={`/organisations/${user?.organization?.slug}/settings`}
														className="text-white/80 hover:text-white"
													>
														Organisation Settings
													</Link>
												</DropdownMenuItem>
												<DropdownMenuItem asChild>
													<Link
														to={`/organisations/${user?.organization?.slug}/dashboard`}
														className="text-white/80 hover:text-white"
													>
														Dashboard
													</Link>
												</DropdownMenuItem>
											</>
										)}

										{(bestRole === 'SUPER_ADMIN' ||
											bestRole === 'SYSTEM_ADMIN') && (
												<>
													<DropdownMenuItem asChild>
														<Link
															to="/settings/profile"
															className="text-white/80 hover:text-white"
														>
															Edit Profile
														</Link>
													</DropdownMenuItem>
													<DropdownMenuItem asChild>
														<Link
															to="/admin/dashboard"
															className="text-white/80 hover:text-white"
														>
															Dashboard
														</Link>
													</DropdownMenuItem>
												</>
											)}

										<DropdownMenuSeparator className="bg-background-dark/60" />
										<DropdownMenuItem
											className="text-red-400 focus:bg-red-600 focus:text-white"
											onClick={(e) => {
												e.preventDefault()
												submit(formRef.current)
											}}
										>
											<Form
												ref={formRef}
												className="flex w-full items-center"
												action="/logout"
												method="POST"
											>
												Sign out
											</Form>
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							</div>
						) : (
							<div className="flex items-center space-x-2">
								<Link
									to="/login"
									className="rounded-md bg-background-dark-foreground/60 px-3 py-1.5 text-sm font-medium text-white/80 transition-all duration-200 hover:bg-background-dark hover:text-white/90"
								>
									Sign in
								</Link>
								<Link
									to="/signup"
									className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white transition-all duration-200 hover:bg-primary/90"
								>
									Sign up
								</Link>
							</div>
						)}
					</div>

					{/* Mobile Menu Button */}
					<button
						className="rounded-md bg-background-dark-foreground/60 p-1.5 text-white/70 hover:text-white md:hidden"
						onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
					>
						<Icon
							name={isMobileMenuOpen ? 'close' : 'menu'}
							className="size-5"
						/>
					</button>
				</nav>
			</motion.header>

			{/* Mobile Menu */}
			{isMobileMenuOpen && (
				<div className="fixed inset-0 z-[100] md:hidden">
					<div
						className="absolute inset-0 bg-black/60 backdrop-blur-sm"
						onClick={() => setIsMobileMenuOpen(false)}
					/>
					<div className="absolute inset-y-0 right-0 w-full max-w-sm bg-background-dark shadow-xl">
						<div className="flex h-16 items-center justify-between border-b border-background-dark-foreground/30 px-4">
							<Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
								<img
									src="/img/logos/logo.png"
									alt="7even Sports"
									className="h-8"
								/>
							</Link>
							<button
								onClick={() => setIsMobileMenuOpen(false)}
								className="rounded-md bg-background-dark-foreground/50 p-1.5 text-white/70 hover:text-white"
							>
								<Icon name="close" className="size-4" />
							</button>
						</div>

						<div className="h-[calc(100vh-4rem)] overflow-y-auto p-4">
							<ul className="space-y-1">
								{navItems.map((item) => (
									<li key={item.to}>
										{'dropdown' in item && item.dropdown ? (
											<>
												<Link
													to={item.to}
													onClick={() => setIsMobileMenuOpen(false)}
													className="flex items-center justify-between rounded-md px-3 py-2 text-sm font-medium text-white/90 hover:bg-background-dark-foreground/70"
												>
													<span>{item.label}</span>
													<Icon
														name="chevron-right"
														className="size-4 text-white/60"
													/>
												</Link>
												<div className="ml-3 mt-1 space-y-1 border-l border-background-dark-foreground/30 pl-3">
													{item.dropdown.map((subItem) => (
														<Link
															key={subItem.to}
															to={subItem.to}
															onClick={() => setIsMobileMenuOpen(false)}
															className="block rounded-md px-3 py-1.5 text-sm text-white/70 hover:bg-background-dark-foreground/50 hover:text-white/90"
														>
															{subItem.label}
														</Link>
													))}
												</div>
											</>
										) : (
											<NavLink
												to={item.to}
												onClick={() => setIsMobileMenuOpen(false)}
												className={({ isActive }) =>
													`flex items-center rounded-md px-3 py-2 text-sm font-medium ${isActive
														? 'bg-background-dark-foreground text-white/90'
														: 'text-white/70 hover:bg-background-dark-foreground/70 hover:text-white/90'
													}`
												}
											>
												{item.label}
											</NavLink>
										)}
									</li>
								))}
							</ul>

							{user ? (
								<div className="mt-4 border-t border-background-dark-foreground/30 pt-4">
									<div className="mb-3 flex items-center gap-3">
										{user.avatar ? (
											<img
												src={user.avatar}
												alt={user.name}
												className="h-8 w-8 rounded-full object-cover ring-1 ring-background-dark-foreground"
											/>
										) : (
											<div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white ring-1 ring-background-dark-foreground">
												{user.name?.charAt(0).toUpperCase()}
											</div>
										)}
										<div>
											<p className="font-medium text-white/90">{user.name}</p>
											<p className="text-xs text-white/60">{user.email}</p>
										</div>
									</div>

									<Form
										action="/logout"
										method="POST"
										className="w-full"
										onSubmit={() => setIsMobileMenuOpen(false)}
									>
										<button
											type="submit"
											className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-red-400 transition-colors hover:bg-background-dark-foreground/50 hover:text-red-300"
										>
											<Icon name="exit" className="size-4" />
											Sign out
										</button>
									</Form>
								</div>
							) : (
								<div className="mt-4 space-y-2 border-t border-background-dark-foreground/30 pt-4">
									<Link
										to="/login"
										onClick={() => setIsMobileMenuOpen(false)}
										className="block w-full rounded-md bg-background-dark-foreground/60 px-3 py-2 text-center text-sm font-medium text-white/80 transition-colors hover:bg-background-dark hover:text-white/90"
									>
										Sign in
									</Link>
									<Link
										to="/signup"
										onClick={() => setIsMobileMenuOpen(false)}
										className="block w-full rounded-md bg-primary px-3 py-2 text-center text-sm font-medium text-white transition-colors hover:bg-primary/90"
									>
										Sign up
									</Link>
								</div>
							)}
						</div>
					</div>
				</div>
			)}
		</>
	)
}

export default ModernNav
